import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { calcEmployeeBf, type EmployeeBfNumbers } from "@/lib/employee-bf-calc";
import type {
  BfTargetSalesUnit,
  DashboardOwner,
  DashboardProject,
} from "@/lib/dashboard-bf-calc";

export interface EmployeeBf extends EmployeeBfNumbers {
  /** Navn på enheten(e) brukeren er medlem av for året. */
  unitNames: string[];
}

interface UnitRow {
  id: string;
  name: string;
  bf_target_mnok: number | null;
  year: number;
  members: { user_id: string }[];
}

interface OwnerRow {
  user_id: string;
  project_id: string;
  sales_unit_id: string | null;
  ownership_pct: number | null;
  projects: {
    id: string;
    project_code: string | null;
    is_internal: boolean | null;
    project_status: string | null;
    bf_actual: number | null;
    bf_estimated: number | null;
    completed_at: string | null;
    created_at: string;
    estimated_deadline: string | null;
    deleted_at: string | null;
  } | null;
}

const EMPTY: EmployeeBf = {
  hasUnit: false,
  unitNames: [],
  targetKr: 0,
  unitLevertKr: 0,
  achieved: false,
  monthBidragKr: 0,
  ytdBidragKr: 0,
};

/**
 * BF-tall for profilsidens MaalTab (VIB-632): personens bidrag (måned/YTD)
 * og ENHETENS delte måloppnåelse — målsemantikken låst i VIB-1285. Ingen egen
 * måltabell: målet utledes fra `sales_units.bf_target_mnok` via medlemskap.
 *
 * Leser project_owners bundet på enhetens medlemmer (.in user_id) — enhetens
 * samlede oppnåelse trenger ALLE medlemmenes rader, ikke bare den viste
 * brukerens. All matte ligger i employee-bf-calc (ren, testet).
 */
export function useEmployeeBf(userId: string, year: number) {
  return useQuery({
    queryKey: ["employee-bf", userId, year],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<EmployeeBf> => {
      const { data: units, error: unitErr } = await supabase
        .from("sales_units")
        .select("id, name, bf_target_mnok, year, members:sales_unit_members(user_id)")
        .eq("year", year);
      if (unitErr) throw unitErr;

      const myUnits = ((units ?? []) as unknown as UnitRow[]).filter((u) =>
        (u.members ?? []).some((m) => m.user_id === userId),
      );
      if (myUnits.length === 0) return EMPTY;

      const memberIds = [...new Set(myUnits.flatMap((u) => u.members.map((m) => m.user_id)))];

      // Bundet på enhetens medlemmer — genuint under 1000 rader (se paginate-allowlist).
      const { data: ownerRows, error: ownerErr } = await supabase
        .from("project_owners")
        .select(
          "user_id, project_id, sales_unit_id, ownership_pct, projects(id, project_code, is_internal, project_status, bf_actual, bf_estimated, completed_at, created_at, estimated_deadline, deleted_at)",
        )
        .in("user_id", memberIds);
      if (ownerErr) throw ownerErr;

      const projectsById = new Map<string, DashboardProject>();
      const owners: DashboardOwner[] = [];
      for (const row of (ownerRows ?? []) as unknown as OwnerRow[]) {
        const p = row.projects;
        // Soft-delete: slettede prosjekter skal aldri telle i aggregater.
        if (!p || p.deleted_at) continue;
        projectsById.set(p.id, {
          id: p.id,
          project_code: p.project_code,
          is_internal: p.is_internal,
          project_status: p.project_status ?? "",
          total_amount: null,
          bf_estimated: p.bf_estimated,
          bf_actual: p.bf_actual,
          completed_at: p.completed_at,
          created_at: p.created_at,
          estimated_deadline: p.estimated_deadline,
        });
        owners.push({
          project_id: row.project_id,
          user_id: row.user_id,
          sales_unit_id: row.sales_unit_id,
          ownership_pct: row.ownership_pct,
        });
      }

      const salesUnits: BfTargetSalesUnit[] = myUnits.map((u) => ({
        id: u.id,
        bf_target_mnok: u.bf_target_mnok,
        year: u.year,
        memberUserIds: u.members.map((m) => m.user_id),
      }));

      const now = new Date();
      const numbers = calcEmployeeBf({
        projects: [...projectsById.values()],
        owners,
        salesUnits,
        userId,
        year,
        month: now.getMonth() + 1,
      });
      return { ...numbers, unitNames: myUnits.map((u) => u.name) };
    },
  });
}
